// MAP-TYPE-TOGGLE — Aurora-styled segmented "Map / Satellite" control that
// replaces the native Google-Maps map-type box (the white "Map | Satellite"
// widget). Presentational only: the parent owns the map-type state and passes
// the resolved Google `mapTypeId` to <Map>. Positioned by the parent as an
// absolute overlay (mirrors the "Edit layers" overlay in PropertyMapContent).
import React from 'react';

/**
 * Google Maps base map-type ids this toggle switches between. We model the two
 * user-facing choices ("Map" / "Satellite") rather than the full Google enum so
 * each surface can map them to whichever concrete ids it wants (e.g. "terrain"
 * vs "roadmap" for Map, "satellite" vs "hybrid" for Satellite).
 */
export type MapTypeToggleValue = 'roadmap' | 'satellite';

export interface MapTypeToggleProps {
  /** Which segment is active. */
  value: MapTypeToggleValue;
  /** Fired with the newly-selected value when a segment is clicked. */
  onChange: (value: MapTypeToggleValue) => void;
  /** Extra classes for the positioning wrapper (e.g. absolute top/left). */
  className?: string;
  /**
   * Fill the full width of the parent, splitting the two segments evenly
   * (each `flex-1`). Use inside a panel control group so the segments occupy
   * the whole row instead of leaving a dark empty gap beside them. Default
   * (false) keeps the compact, content-width overlay pill.
   */
  block?: boolean;
  /** Accessible label for the group. Defaults to "Map type". */
  ariaLabel?: string;
  'data-testid'?: string;
}

const SEGMENTS: ReadonlyArray<{ value: MapTypeToggleValue; label: string }> = [
  { value: 'roadmap', label: 'Map' },
  { value: 'satellite', label: 'Satellite' },
];

/**
 * Two-segment Aurora control. Active segment gets the app-wide accent
 * selected treatment (`selected-accent`: accent fill + on-accent text +
 * Aurora glow); the wrapper uses the translucent surface + backdrop-blur so
 * map tiles remain partially visible behind it, matching the existing
 * overlay buttons.
 */
export function MapTypeToggle({
  value,
  onChange,
  className,
  block = false,
  ariaLabel = 'Map type',
  'data-testid': testId = 'map-type-toggle',
}: MapTypeToggleProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`${block ? 'flex w-full' : 'inline-flex'} items-center gap-0.5 rounded-md border border-border bg-surface-1/80 p-0.5 backdrop-blur shadow-sm ${className ?? ''}`}
    >
      {SEGMENTS.map((segment) => {
        const active = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (!active) onChange(segment.value);
            }}
            data-testid={`${testId}-${segment.value}`}
            className={`rounded-sm px-2.5 py-1 text-xs font-semibold transition-colors ${
              block ? 'flex-1 text-center' : ''
            } ${
              active
                ? 'border border-transparent selected-accent'
                : 'border border-transparent text-text-secondary hover:text-text-primary hover:bg-state-hover'
            }`}
          >
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}
